import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { Trash2, Upload, Eye, Edit, Plus } from 'lucide-react';
import LunchMenuEditor from './LunchMenuEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activeTab, setActiveTab] = useState('orders'); // Changed default to 'orders' so orders show immediately
  const [isLoading, setIsLoading] = useState(true);
  const [authToken] = useState(localStorage.getItem('adminToken') || 'M7XOM9bRzKFdC7cbmS16Y5pUtgXi3KL6');
  const { toast } = useToast();
  const navigate = useNavigate();

  // Check authentication on mount
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin-login');
    }
  }, [navigate]);

  // Menu form state
  const [menuForm, setMenuForm] = useState({
    name: '',
    description: '',
    price: '',
    category: 'antipasti',
    available: true
  });
  
  const [editingItem, setEditingItem] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'tasting', label: 'Tasting Menu' },
    { value: 'antipasti', label: 'Antipasti' },
    { value: 'primi', label: 'Primi Piatti' },
    { value: 'secondi', label: 'Secondi Piatti' },
    { value: 'dolci', label: 'Dolci' }
  ];

  const photoCategories = ['hero', 'menu', 'gallery', 'location', 'chef', 'interior'];

  useEffect(() => {
    loadMenuItems();
    loadPhotos();
    loadOrders(); // Always load orders on mount
  }, [selectedCategory]);

  // Reload orders when tab changes to orders
  useEffect(() => {
    if (activeTab === 'orders') {
      loadOrders();
    }
  }, [activeTab]);

  const loadMenuItems = async () => {
    try {
      const url = selectedCategory === 'all' ? 
        `${API}/menu/items` : 
        `${API}/menu/items?category=${selectedCategory}`;
      
      const response = await fetch(url);
      const data = await response.json();
      setMenuItems(data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load menu items', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const loadPhotos = async () => {
    try {
      const response = await fetch(`${API}/photos`);
      const data = await response.json();
      setPhotos(data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load photos', variant: 'destructive' });
    }
  };

  const loadOrders = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/orders`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOrders(data);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load orders', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus, estimatedMinutes = null) => {
    try {
      let url = `${API}/orders/${orderId}/status?status=${newStatus}`;
      if (estimatedMinutes) {
        url += `&estimatedMinutes=${estimatedMinutes}`;
      }
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        let description = `Order status updated to ${newStatus}`;
        if (newStatus === 'confirmed') {
          description = `✅ Order confirmed! Customer notified: "Food will arrive in ~${estimatedMinutes} minutes"`;
        } else if (newStatus === 'ready') {
          description = '✅ Order marked as READY! Customer has been notified.';
        } else if (newStatus === 'out_for_delivery') {
          description = `🚗 Driver left! Customer notified: "Food will arrive in ~${estimatedMinutes} minutes"`;
        }
        
        toast({ title: 'Success', description });
        loadOrders();
      } else {
        throw new Error('Failed to update order status');
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleSubmitMenuItem = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const url = editingItem ? `${API}/menu/items/${editingItem.id}` : `${API}/menu/items`;
      const method = editingItem ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(menuForm)
      });
      
      if (response.ok) {
        toast({ 
          title: 'Success', 
          description: `Menu item ${editingItem ? 'updated' : 'created'} successfully` 
        });
        setMenuForm({ name: '', description: '', price: '', category: 'antipasti', available: true });
        setEditingItem(null);
        loadMenuItems();
      } else {
        throw new Error('Failed to save menu item');
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteMenuItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this menu item?')) return;
    
    try {
      const response = await fetch(`${API}/menu/items/${itemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        toast({ title: 'Success', description: 'Menu item deleted successfully' });
        loadMenuItems();
      } else {
        throw new Error('Failed to delete menu item');
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleFileUpload = async (file, category = 'gallery') => {
    setUploadingFile(true);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    
    try {
      const response = await fetch(`${API}/upload/photo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
        body: formData
      });
      
      if (response.ok) {
        const result = await response.json();
        toast({ title: 'Success', description: 'Photo uploaded successfully' });
        loadPhotos();
        return result;
      } else {
        throw new Error('Failed to upload photo');
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setUploadingFile(false);
    }
  };

  const assignPhotoToMenuItem = async (itemId, photoId) => {
    try {
      const response = await fetch(`${API}/menu/items/${itemId}/photo/${photoId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        toast({ title: 'Success', description: 'Photo assigned to menu item' });
        loadMenuItems();
      } else {
        throw new Error('Failed to assign photo');
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleEditMenuItem = (item) => {
    setEditingItem(item);
    setMenuForm({
      name: item.name,
      description: item.description,
      price: item.price,
      category: item.category,
      available: item.available
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Header */}
      <nav className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_fine-dining-vaasa/artifacts/6i8d0v30_FB_IMG_1759419152155.jpg" 
              alt="Sunsari Restaurant" 
              className="h-10 w-auto"
            />
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Admin Panel</h1>
              <p className="text-sm text-gray-600">Sunsari Restaurant Management</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">
              👤 {localStorage.getItem('adminUser') || 'Admin'}
            </span>
            <button 
              onClick={() => {
                localStorage.removeItem('adminToken');
                localStorage.removeItem('adminUser');
                navigate('/admin-login');
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
            >
              Logout
            </button>
            <button 
              onClick={() => navigate('/')}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
            >
              Back to Website
            </button>
          </div>
        </div>
      </nav>
      
      <div className="max-w-7xl mx-auto p-6">
        {/* Tab Navigation */}
        <div className="flex space-x-4 mb-8 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('menu')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'menu'
                ? 'text-amber-600 border-b-2 border-amber-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Menu Management
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'orders'
                ? 'text-amber-600 border-b-2 border-amber-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Orders {orders.length > 0 && `(${orders.filter(o => o.status === 'pending').length})`}
          </button>
          <button
            onClick={() => setActiveTab('lunch')}
            data-testid="admin-tab-lunch"
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'lunch'
                ? 'text-amber-600 border-b-2 border-amber-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Lounaslista
          </button>
        </div>

        {activeTab === 'lunch' && <LunchMenuEditor />}

        {activeTab === 'menu' && (
          <>
            <div className="mb-8">
              <h2 className="text-2xl font-light text-gray-900 mb-2">Content Management</h2>
              <p className="text-gray-600">Manage menu items and photos</p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
          {/* Menu Item Form */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  {editingItem ? <Edit className="w-5 h-5 mr-2" /> : <Plus className="w-5 h-5 mr-2" />}
                  {editingItem ? 'Edit Menu Item' : 'Add Menu Item'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmitMenuItem} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Dish Name</Label>
                    <Input
                      id="name"
                      value={menuForm.name}
                      onChange={(e) => setMenuForm({...menuForm, name: e.target.value})}
                      placeholder="e.g., Truffle Risotto"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={menuForm.description}
                      onChange={(e) => setMenuForm({...menuForm, description: e.target.value})}
                      placeholder="Describe the dish..."
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="price">Price</Label>
                    <Input
                      id="price"
                      value={menuForm.price}
                      onChange={(e) => setMenuForm({...menuForm, price: e.target.value})}
                      placeholder="e.g., €25"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Select 
                      value={menuForm.category}
                      onValueChange={(value) => setMenuForm({...menuForm, category: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.slice(1).map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex justify-between">
                    <Button type="submit" disabled={isLoading}>
                      {editingItem ? 'Update Item' : 'Add Item'}
                    </Button>
                    {editingItem && (
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => {
                          setEditingItem(null); 
                          setMenuForm({name: '', description: '', price: '', category: 'antipasti', available: true});
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Photo Upload */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Upload className="w-5 h-5 mr-2" />
                  Upload Photo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleFileUpload(file, 'menu');
                    }}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                    disabled={uploadingFile}
                  />
                  {uploadingFile && <p className="text-sm text-gray-500">Uploading...</p>}
                </div>
                
                <div className="mt-4">
                  <Label>Recent Photos</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {photos.slice(0, 6).map(photo => (
                      <img
                        key={photo.id}
                        src={`${BACKEND_URL}${photo.url}`}
                        alt={photo.original_name}
                        className="w-full h-16 object-cover rounded border cursor-pointer hover:border-blue-500"
                        onClick={() => {
                          // Logic to assign photo to selected menu item
                        }}
                      />
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Menu Items List */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Menu Items</CardTitle>
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <p>Loading menu items...</p>
                ) : (
                  <div className="space-y-4">
                    {menuItems.map(item => (
                      <div key={item.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h3 className="font-medium text-lg">{item.name}</h3>
                              <Badge variant="secondary">{item.category}</Badge>
                              <span className="text-lg font-semibold text-green-600">{item.price}</span>
                            </div>
                            <p className="text-gray-600 text-sm mb-3">{item.description}</p>
                            {item.image_url && (
                              <img 
                                src={`${BACKEND_URL}${item.image_url}`}
                                alt={item.name}
                                className="w-20 h-20 object-cover rounded"
                              />
                            )}
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditMenuItem(item)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteMenuItem(item.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {menuItems.length === 0 && (
                      <p className="text-center text-gray-500 py-8">No menu items found.</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
          </>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-6">
            <div className="mb-8">
              <h2 className="text-2xl font-light text-gray-900 mb-2">Food Orders</h2>
              <p className="text-gray-600">Manage customer orders and send notifications</p>
            </div>

            {isLoading ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Loading orders...</p>
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <p className="text-gray-500">No orders yet</p>
              </div>
            ) : (
              <div className="grid gap-6">
                {orders.map((order) => (
                  <Card key={order.id} className="overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-lg">{order.customer.name}</CardTitle>
                          <p className="text-sm text-gray-600 mt-1">
                            📞 {order.customer.phone} • 📧 {order.customer.email || 'No email'}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            📍 {order.customer.address} ({order.customer.distance || 'N/A'} km)
                          </p>
                        </div>
                        <Badge 
                          className={`${
                            order.status === 'pending' ? 'bg-yellow-500' :
                            order.status === 'confirmed' ? 'bg-blue-500' :
                            order.status === 'preparing' ? 'bg-purple-500' :
                            order.status === 'ready' ? 'bg-green-500' :
                            order.status === 'out_for_delivery' ? 'bg-indigo-500' :
                            order.status === 'delivered' ? 'bg-gray-500' :
                            'bg-red-500'
                          } text-white`}
                        >
                          {order.status.toUpperCase().replace('_', ' ')}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                      {/* Order Items */}
                      <div className="mb-4">
                        <h4 className="font-semibold text-sm text-gray-700 mb-2">Order Items:</h4>
                        <ul className="space-y-1">
                          {order.items.map((item, idx) => (
                            <li key={idx} className="text-sm text-gray-600 flex justify-between">
                              <span>• {item.name} x{item.quantity}</span>
                              <span>{item.price}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Pricing */}
                      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                        <div className="flex justify-between text-sm text-gray-600 mb-1">
                          <span>Subtotal:</span>
                          <span>{order.subtotal}€</span>
                        </div>
                        <div className="flex justify-between text-sm text-gray-600 mb-1">
                          <span>Delivery Fee:</span>
                          <span>{order.deliveryFee}€</span>
                        </div>
                        <div className="flex justify-between font-bold text-lg mt-2 pt-2 border-t border-gray-200">
                          <span>Total:</span>
                          <span className="text-amber-600">{order.totalPrice}€</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Payment: {order.paymentMethod}</p>
                      </div>

                      {/* Special Instructions */}
                      {order.customer.specialInstructions && (
                        <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-sm font-semibold text-blue-900">Special Instructions:</p>
                          <p className="text-sm text-blue-800">{order.customer.specialInstructions}</p>
                        </div>
                      )}

                      {/* Order Date */}
                      <p className="text-xs text-gray-500 mb-4">
                        Order placed: {new Date(order.created_at).toLocaleString()}
                      </p>

                      {/* Status Update Buttons */}
                      <div className="flex flex-wrap gap-2">
                        {order.status === 'pending' && (
                          <div className="space-y-2">
                            <div className="flex gap-2 items-end">
                              <div className="flex-1">
                                <label className="text-xs text-gray-600 block mb-1">Estimated Prep + Delivery (minutes)</label>
                                <input
                                  type="number"
                                  id={`prep-time-${order.id}`}
                                  placeholder="e.g., 60"
                                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                                  min="10"
                                  max="180"
                                />
                              </div>
                              <Button 
                                onClick={() => {
                                  const estimatedMinutes = document.getElementById(`prep-time-${order.id}`).value;
                                  if (!estimatedMinutes || estimatedMinutes < 10) {
                                    alert('Please enter estimated time (minimum 10 minutes)');
                                    return;
                                  }
                                  updateOrderStatus(order.id, 'confirmed', estimatedMinutes);
                                }}
                                className="bg-blue-500 hover:bg-blue-600"
                              >
                                Confirm Order
                              </Button>
                            </div>
                            <p className="text-xs text-gray-500">Customer will receive estimated delivery time</p>
                          </div>
                        )}
                        {order.status === 'confirmed' && (
                          <Button 
                            onClick={() => updateOrderStatus(order.id, 'preparing')}
                            className="bg-purple-500 hover:bg-purple-600"
                          >
                            Start Preparing
                          </Button>
                        )}
                        {order.status === 'preparing' && (
                          <Button 
                            onClick={() => updateOrderStatus(order.id, 'ready')}
                            className="bg-green-500 hover:bg-green-600 text-white font-bold"
                          >
                            🔔 Mark Ready & Notify Customer
                          </Button>
                        )}
                        {order.status === 'ready' && (
                          <div className="space-y-2">
                            <div className="flex gap-2 items-end">
                              <div className="flex-1">
                                <label className="text-xs text-gray-600 block mb-1">Delivery Time (minutes)</label>
                                <input
                                  type="number"
                                  id={`delivery-time-${order.id}`}
                                  placeholder="e.g., 20"
                                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                                  min="5"
                                  max="60"
                                />
                              </div>
                              <Button 
                                onClick={() => {
                                  const deliveryMinutes = document.getElementById(`delivery-time-${order.id}`).value;
                                  if (!deliveryMinutes || deliveryMinutes < 5) {
                                    alert('Please enter delivery time (minimum 5 minutes)');
                                    return;
                                  }
                                  updateOrderStatus(order.id, 'out_for_delivery', deliveryMinutes);
                                }}
                                className="bg-indigo-500 hover:bg-indigo-600"
                              >
                                Driver Left
                              </Button>
                            </div>
                            <p className="text-xs text-gray-500">Customer will receive delivery ETA</p>
                          </div>
                        )}
                        {order.status === 'out_for_delivery' && (
                          <Button 
                            onClick={() => updateOrderStatus(order.id, 'delivered')}
                            className="bg-gray-500 hover:bg-gray-600"
                          >
                            Mark as Delivered
                          </Button>
                        )}
                        
                        {/* Call Customer Button */}
                        <a 
                          href={`tel:${order.customer.phone}`}
                          className="inline-flex items-center px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-md font-medium transition-colors"
                        >
                          📞 Call Customer
                        </a>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;